import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

import { Page } from "modules/layout/Page";
import firebase from "firebaseFactory";
import { FormSingleFieldRow, FormLabel } from "components/Form";
import { useFirebaseListFunction, useCachedAsyncData } from "hooks";
import { DeviceReference } from "modules/devices";
import { Heading2 } from "components/Heading2";
import { groupByField } from "extensions/groupBy";
import { useAuthDataContext, Permissions } from "modules/auth";
import {
  AsyncApiParameterSchemas,
  DynamicForm,
  DynamicFormData,
} from "components/DynamicForm";

export interface SendSiteCommandResult {
  payload: {
    timestamp_utc: string;
    request_id: string;
    message_type: string;
    unit_id: string;
  };
}
const uiSchemaUrl =
  "https://relectrify.github.io/Code-Interfaces-MQTT/ui_schemas/commands.json";

interface CommandSchemas {
  [commandId: string]: {
    command_type: string;
    message_type: string;
    parameters: AsyncApiParameterSchemas;
  };
}

export const SendCommandPage = ({
  sendCommand = firebase.function("sendCommand"),
  getDevicesBySiteId = firebase.function("getDevicesBySiteId"),
}) => {
  const { siteId, unitId, deviceId } = useParams();
  const { enforcer } = useAuthDataContext();
  const [selectedCommandId, setSelectedCommandId] = useState<string>();

  const requiredPermission = deviceId
    ? Permissions.Devices_SendCommand
    : unitId
    ? Permissions.Unit_SendCommand
    : Permissions.Site_SendCommand;
  const filterPermittedCommandSchemas = (schemas: CommandSchemas) => {
    return Object.fromEntries(
      Object.entries(schemas).filter(([key, value]) =>
        enforcer.can(requiredPermission, {
          siteId,
          unitId,
          deviceId,
          messageType: value.message_type,
          commandType: value.command_type,
        })
      )
    );
  };

  const {
    data: commandSchemas,
    isLoading: isLoadingCommandSchemas,
  } = useCachedAsyncData<CommandSchemas | undefined>(
    async () => {
      const response = await axios.get(uiSchemaUrl);
      return filterPermittedCommandSchemas(response.data);
    },
    uiSchemaUrl,
    { initialData: undefined }
  );
  const [status, setStatus] = useState<string>();
  const [commandSent, setCommandSent] = useState<SendSiteCommandResult>();
  const [commandReceived, setCommandReceived] = useState<unknown>();
  const [commandResponse, setCommandResponse] = useState<{
    [deviceId: string]: unknown;
  }>();
  const [commandKeyStartAt] = useState<string | null>(
    firebase.database(firebase.shards.Customer).ref().push().key
  );

  const { data: devicesInSite, isLoading } = useFirebaseListFunction<
    DeviceReference
  >(getDevicesBySiteId, { siteId });

  const targetDevices = devicesInSite.filter(
    (x) =>
      (!unitId || x.unitId === unitId) && (!deviceId || x.deviceId === deviceId)
  );
  const targetDevicesByUnit = groupByField(targetDevices, "unitId");

  useEffect(() => {
    if (!commandSent) return;

    const commandRef = firebase
      .database(firebase.shards.Customer)
      .ref(
        `devices/dataBySite/${siteId}/historyCombined/command/${commandSent.payload.message_type}`
      );
    const commandListener = commandRef
      .orderByKey()
      .startAt(commandKeyStartAt)
      .on("child_added", (snapshot) => {
        // console.log("Listening command");
        const val = snapshot.val();
        if (
          commandSent?.payload &&
          val.timestamp_utc === commandSent.payload.timestamp_utc &&
          val.request_id === commandSent.payload.request_id
        ) {
          setCommandReceived(val);
          setStatus((status) =>
            status === "Sending command..."
              ? "Command sent. Waiting for responses..."
              : status
          );
        }
      });
    const responseRef = firebase
      .database(firebase.shards.Customer)
      .ref(
        `devices/dataBySite/${siteId}/historyCombined/response/${commandSent.payload.message_type}`
      );
    const responseListener = responseRef
      .orderByKey()
      .startAt(commandKeyStartAt)
      .on("child_added", (snapshot) => {
        const val = snapshot.val();
        const nResponseCaptured: number = Object.keys(commandResponse || {})
          .length;
        if (nResponseCaptured >= targetDevices.length) {
          // console.log('All response received')
          setStatus(
            `${nResponseCaptured}/${targetDevices.length} Last Response received at ${val.request.timestamp_utc} from ${val.device_id}`
          );
          return;
        }
        if (
          commandSent?.payload &&
          val.request.timestamp_utc === commandSent.payload.timestamp_utc &&
          val.request.request_id === commandSent.payload.request_id
        ) {
          setCommandResponse((x) => ({ ...x, [val.device_id]: val }));
          setStatus(
            `${nResponseCaptured}/${targetDevices.length} Last Response received at ${val.request.timestamp_utc} from ${val.device_id}`
          );
        }
      });
    return () => {
      commandRef.off("child_added", commandListener);
      responseRef.off("child_added", responseListener);
    };
  }, [
    commandKeyStartAt,
    commandResponse,
    commandSent,
    siteId,
    targetDevices.length,
  ]);

  const onClickSubmit = async (parameters: DynamicFormData) => {
    if (!commandSchemas || !selectedCommandId) return;
    setStatus("Sending command...");
    setCommandSent(undefined);
    setCommandReceived(undefined);
    setCommandResponse(undefined);
    // setCommandKeyStartAt(
    //   firebase.database(firebase.shards.Customer).ref().push().key
    // );
    const result = await sendCommand.action({
      messageType: commandSchemas[selectedCommandId].message_type,
      commandType: commandSchemas[selectedCommandId].command_type,
      siteId,
      unitId,
      deviceId,
      parameters: parameters,
    });
    setCommandSent(result.data);
  };

  return (
    <Page status={status}>
      <FormSingleFieldRow>
        <FormLabel label="Command" htmlFor="selectedCommand" />
        <select
          name="selectedCommand"
          id="selectedCommand"
          defaultValue={selectedCommandId}
          onChange={(e) => {
            setSelectedCommandId(e.target.value);
          }}
          className="block appearance-none bg-white border border-gray-400 w-full hover:border-gray-500 px-4 py-2 pr-8 rounded shadow leading-tight focus:outline-none focus:shadow-outline"
        >
          <option value="">
            ({isLoadingCommandSchemas ? "Loading..." : "Select a command"})
          </option>
          {commandSchemas &&
            Object.entries(commandSchemas).map(([uniqueId, value], index) => (
              <option key={index} value={uniqueId}>
                {value.message_type} - {value.command_type}
              </option>
            ))}
        </select>
      </FormSingleFieldRow>
      {commandSchemas && selectedCommandId && (
        <DynamicForm
          onSubmit={onClickSubmit}
          isLoading={isLoading}
          formSchema={commandSchemas[selectedCommandId].parameters}
          submitButtonText="Send command"
        />
      )}
      <div className="mt-6">
        <Heading2>Responses</Heading2>
        <div className="flex">
          {Object.entries(targetDevicesByUnit).map(
            ([unitId, devices], index) => (
              <div className={`px-2 w-full md:w-1/2 lg:w-1/3`} key={index}>
                <div className="rounded-lg shadow-sm bg-white mb-6">
                  <div className="px-6 py-4">
                    <Heading2 className="font-bold text-xl mb-2">
                      {unitId}
                    </Heading2>
                    <div className="flex">
                      {devices.map((device, index) => (
                        <div
                          key={index}
                          className={`rounded-full w-4 h-4 py-1 px-1 mr-2 ${
                            !commandResponse?.[device.deviceId]
                              ? "bg-gray-400"
                              : (commandResponse?.[device.deviceId] as {
                                  response: number;
                                })?.response === 0
                              ? "bg-green-400"
                              : "bg-red-400"
                          }`}
                          title={`Device ${device.deviceId}`}
                        ></div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )
          )}
        </div>
      </div>
      <div className="mt-6">
        {enforcer.can(Permissions.Devices_ManageAcrossAllSites) && (
          <>
            <Heading2>Diagnostics</Heading2>
            <h3 className="text-lg font-bold mt-3 mb-2">Command sent</h3>
            <div className="px-6 py-4 whitespace-pre text-sm leading-5">
              {JSON.stringify(commandSent, null, 2)}
            </div>
            <h3 className="text-lg font-bold mt-3 mb-2">
              Command recorded on MQTT broker
            </h3>
            <div className="px-6 py-4 whitespace-pre text-sm leading-5">
              {JSON.stringify(commandReceived, null, 2)}
            </div>
            <h3 className="text-lg font-bold mt-3 mb-2">Responses received</h3>
            {commandResponse &&
              Object.entries(commandResponse).map(
                ([deviceId, response], index) => (
                  <div key={index}>
                    <span className="block text-sm font-bold mb-2 mt-3">
                      {deviceId}
                    </span>
                    <div className="px-6 py-4 whitespace-pre text-sm leading-5">
                      {JSON.stringify(response, null, 2)}
                    </div>
                  </div>
                )
              )}
          </>
        )}
      </div>
    </Page>
  );
};
